// Device detection utility
const getDevice = (): string => {
  if (typeof window === "undefined") return "unknown";

  const width = window.innerWidth;
  if (width < 780) return "mobile";
  if (width < 1440) return "tablet";
  return "desktop";
};

export const trackCtaClick = ({
  event = "ctaClick",
  section,
  ctaText,
  config,
  contentDescription,
  link,
  ...rest
}: {
  event?: "ctaClick" | "download_app_click";
  section: string;
  ctaText: string;
  config: string;
  contentDescription?: string;
  link?: string;
  feature?: string;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";

  const dynamicEvent =
    ctaText.trim().toLowerCase() === "download app"
      ? "download_app_click"
      : event;

  const data: Record<string, any> = {
    event: dynamicEvent,
    section,
    ctaText,
    Config: config,
    page,
    pageTitle,
    ...rest,
    date: new Date().toISOString().slice(0, 10),
    time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
  };

  if (contentDescription) data.contentDescription = contentDescription;
  if (link) data.link = link;

  if (process.env.NODE_ENV === "development") {
    console.log("trackCtaClick", data);

    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push(data);
  }
};

export const trackExpandClick = ({
  section,
  innerText,
}: {
  section: string;
  innerText: string;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";

  if (process.env.NODE_ENV === "development") {
    console.log("trackExpandClick", {
      section,
      innerText,
      page,
      pageTitle,
    });

    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push({
      event: "expandClick",
      section,
      innerText,
      page,
      pageTitle,
      interactionType: "expand",
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
  }
};

export const trackFAQClick = ({
  section,
  innerText,
  config,
}: {
  section: string;
  innerText: string;
  config: string;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";

  if (process.env.NODE_ENV === "development") {
    console.log("trackFAQClick", {
      event: "FAQ Click",
      section,
      innerText,
      config,
      page,
      pageTitle,
      interactionType: "expand",
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });

    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push({
      event: "FAQ Click",
      section,
      innerText,
      page,
      pageTitle,
      interactionType: "expand",
      Config: config,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
  }
};

export const trackTermsAgreement = ({
  section,
  config,
  agreement,
}: {
  section: string;
  config: string;
  agreement: boolean;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";

  if (process.env.NODE_ENV === "development") {
    console.log("trackTermsAgreement", {
      event: "termsAgreement",
      section,
      Config: config,
      agreement,
      page,
      pageTitle,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push({
      event: "termsAgreement",
      section,
      Config: config,
      agreement,
      page,
      pageTitle,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
  }
};

export const trackFormSubmit = ({
  section,
  formName,
  formValue,
  config,
  form_id,
  status = "submitted",
  ...rest
}: {
  section: string;
  formName: string;
  formValue: number;
  config: string;
  form_id?: string;
  status?: string;
  [key: string]: string | number | undefined;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";

  // TODO: remove, only for prod testing
  console.log("trackFormSubmit", {
    event: "form_submit",
    section,
    Config: config,
    form_name: section,
    formValue,
    status,
    page,
    pageTitle,
    form_id,
    ...rest,
    date: new Date().toISOString().slice(0, 10),
    time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
  });
  if (process.env.NODE_ENV === "development") {
    console.log("trackFormSubmit", {
      event: "form_submit",
      section,
      Config: config,
      form_name: section,
      formValue,
      status,
      page,
      pageTitle,
      form_id,
      ...rest,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push({
      event: "form_submit",
      section,
      Config: config,
      form_name: formName,
      status,
      formValue,
      page,
      pageTitle,
      form_id,
      ...rest,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
  }
};

export type FormErrorType =
  | "missing_field"
  | "validation_error"
  | "captcha_error"
  | "checkbox_error"
  | "submission_error";

export const trackFormError = ({
  section,
  config,
  formName,
  errorType,
  errorMessage,
  fieldName,
}: {
  section: string;
  config: string;
  formName: string;
  errorType: FormErrorType;
  errorMessage: string;
  fieldName?: string;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";

  const data = {
    event: "formError",
    section,
    Config: config,
    formName,
    formValue: 0,
    errorType,
    errorMessage,
    fieldName,
    page,
    pageTitle,
    date: new Date().toISOString().slice(0, 10),
    time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
  };

  if (process.env.NODE_ENV === "development") {
    console.log("trackFormError", data);
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push(data);
  }
};

export const trackCaseStudyClick = ({
  section,
  activeSlide,
  contentDescription,
  contentType,
  config,
  placement,
}: {
  section: string;
  activeSlide: string;
  contentDescription: string;
  contentType: string;
  config: string;
  placement: string;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";

  if (process.env.NODE_ENV === "development") {
    console.log("trackCaseStudyClick", {
      section,
      activeSlide,
      contentDescription,
      contentType,
      config,
      placement,
      page,
      pageTitle,
    });
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push({
      event: "caseStudyClick",
      section,
      activeSlide,
      contentDescription,
      contentType,
      config,
      placement,
      page,
      pageTitle,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
  }
};

export const trackCarouselNavigation = ({
  section,
  config,
  direction,
  activeSlide,
  contentType,
  placement,
}: {
  section?: string;
  config: string;
  direction: "left" | "right" | "next" | "previous";
  activeSlide:
    | Array<{
        slideIndex: number;
        logoName?: string;
        contentDescription?: string;
        parkingName?: string;
        price?: string;
        distance?: string;
      }>
    | string;
  contentType?: string;
  placement?: string;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";

  if (process.env.NODE_ENV === "development") {
    console.log("trackCarouselNavigation", {
      event: "carouselNavigation",
      Config: config,
      direction,
      activeSlide,
      page,
      pageTitle,
      section,
      contentType,
      placement,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push({
      event: "carouselNavigation",
      Config: config,
      direction,
      activeSlide,
      page,
      pageTitle,
      section,
      contentType,
      placement,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
  }
};

export const trackCarouselNavigationClick = ({
  section,
  config,
  direction,
  activeSlide,
}: {
  section: string;
  config: string;
  direction: "left" | "right";
  activeSlide: Array<{
    slideIndex: number;
    locationName: string;
    price: string;
    contentDescription: string;
  }>;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";

  if (process.env.NODE_ENV === "development") {
    console.log("trackCarouselNavigationClick", {
      event: "carouselNavigationClick",
      section,
      Config: config,
      direction,
      activeSlide,
      page,
      pageTitle,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push({
      event: "carouselNavigationClick",
      section,
      Config: config,
      direction,
      activeSlide,
      page,
      pageTitle,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
  }
};

export const trackContentLinkClick = ({
  section,
  contentTitle,
  config,
}: {
  section: string;
  contentTitle: string;
  config: string;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";

  if (process.env.NODE_ENV === "development") {
    console.log("trackContentLinkClick", {
      config,
      section,
      contentTitle,
      page,
      pageTitle,
    });
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push({
      event: "contentLinkClick",
      config,
      section,
      contentTitle,
      page,
      pageTitle,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
  }
};

export const trackTopLvlLinkClick = ({
  event,
  section,
  innerText,
  href,
}: {
  event: string;
  section: string;
  innerText: string;
  href: string;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";
  const device = getDevice();

  if (process.env.NODE_ENV === "development") {
    console.log("trackTopLvlLinkClick", {
      event,
      section,
      innerText,
      href,
      page,
      pageTitle,
      device,
    });
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push({
      event,
      page,
      section,
      innerText,
      elementType: "Link",
      href,
      pageTitle,
      device,
    });
  }
};

export const trackLogoClick = ({
  section,
  href,
}: {
  section: string;
  href: string;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";
  const device = getDevice();

  if (process.env.NODE_ENV === "development") {
    console.log("trackLogoClick", {
      event: "Logo click",
      section,
      href,
      page,
      pageTitle,
      device,
    });
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push({
      event: "Logo click",
      page,
      section,
      elementType: "Link",
      innerText: "(Logo)",
      href,
      pageTitle,
      device,
    });
  }
};

export const trackHeaderHamburgerClick = () => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";

  if (process.env.NODE_ENV === "development") {
    console.log("trackHeaderHamburgerClick", {
      event: "Header Hamburger Icon Click",
      page,
      section: "Header",
      innerText: "(Hamburger Icon)",
      elementType: "Link Icon",
      pageTitle,
      device: "mobile",
    });
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push({
      event: "Header Hamburger Icon Click",
      page,
      section: "Header",
      innerText: "(Hamburger Icon)",
      elementType: "Link Icon",
      pageTitle,
      device: "mobile",
    });
  }
};

export const trackTimelineClick = ({
  section,
  config,
  stepTitle,
  // stepDescription,
  stepNumber,
}: {
  section: string;
  config: string;
  stepTitle: string;
  stepNumber: string;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";

  if (process.env.NODE_ENV === "development") {
    console.log("trackTimelineClick", {
      section,
      config,
      stepTitle,
      // stepDescription,
      stepNumber,
      page,
      pageTitle,
    });
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push({
      event: "timelineStepClick",
      section,
      config,
      stepTitle,
      // stepDescription,
      stepNumber,
      page,
      pageTitle,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
  }
};

export const trackFormStart = ({
  section,
  config,
  formName,
}: {
  section: string;
  config: string;
  formName?: string;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";

  const data: Record<string, any> = {
    event: "form_start",
    section,
    Config: config,
    page,
    pageTitle,
    date: new Date().toISOString().slice(0, 10),
    time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
  };

  if (formName) data.form_name = formName;

  // TODO: remove, only for prod testing
  console.log("trackFormStart", data);

  if (process.env.NODE_ENV === "development") {
    console.log("trackFormStart", data);
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push(data);
  }
};

export const trackFormInputClick = ({
  section,
  config,
  formName,
  fieldName,
}: {
  section: string;
  config: string;
  formName?: string;
  fieldName?: string;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";

  const data: Record<string, any> = {
    event: "form_input_click",
    section,
    Config: config,
    page,
    pageTitle,
    date: new Date().toISOString().slice(0, 10),
    time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
  };

  if (formName) data.formName = formName;
  if (fieldName) data.fieldName = fieldName;

  // TODO: remove, only for prod testing
  console.log("trackFormInputClick", data);

  if (process.env.NODE_ENV === "development") {
    console.log("trackFormInputClick", data);
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push(data);
  }
};

export const trackCardClick = ({
  section,
  config,
  cardTitle,
  placement,
  contentDescription,
  ctaText,
  link,
}: {
  section: string;
  config: string;
  cardTitle?: string;
  placement?: string;
  contentDescription?: string;
  ctaText?: string;
  link?: string;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";

  const data: Record<string, any> = {
    event: "cardClick",
    section,
    Config: config,
    page,
    pageTitle,
    date: new Date().toISOString().slice(0, 10),
    time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
  };

  if (cardTitle) data.cardTitle = cardTitle;
  if (placement) data.placement = placement;
  if (contentDescription) data.contentDescription = contentDescription;
  if (ctaText) data.ctaText = ctaText;
  if (link) data.link = link;

  if (process.env.NODE_ENV === "development") {
    console.log("trackCardClick", data);
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push(data);
  }
};

export const trackFeatureBoxToggleClick = ({
  section,
  config,
  boxTitle,
}: {
  section: string;
  config: string;
  boxTitle: string;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";

  if (process.env.NODE_ENV === "development") {
    console.log("trackFeatureBoxToggleClick", {
      section,
      config,
      boxTitle,
      page,
      pageTitle,
    });
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push({
      event: "featureBoxToggle",
      section,
      config,
      boxTitle,
      page,
      pageTitle,
      interactionType: "expand",
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
  }
};

export const trackSearchLocation = ({
  section,
  config,
  search_text,
  results_count,
}: {
  section: string;
  config: string;
  search_text: string;
  results_count: number;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";

  if (process.env.NODE_ENV === "development") {
    console.log("trackSearchLocation", {
      event: "search_location",
      section,
      config,
      search_text,
      results_count,
      page,
      pageTitle,
    });
    // DEV MODE: Pause here to inspect analytics data
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push({
      event: "search_location",
      section,
      Config: config,
      search_text,
      results_count,
      page,
      pageTitle,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
  }
};

export const trackParkingFlow = ({
  step,
  section,
  config,
}: {
  step: "enterZone" | "setTime" | "selectVehicle" | "payAndGo";
  section: string;
  config: string;
  valueEntered?: string;
  duration?: string;
  vehicle?: string;
  totalAmount?: string;
  serviceFee?: string;
  paymentStatus?: "success" | "failed";
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";

  const data: Record<string, any> = {
    event: "parkingFlow",
    step,
    section,
    Config: config,
    page,
    pageTitle,
    date: new Date().toISOString().slice(0, 10),
    time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
  };

  if (process.env.NODE_ENV === "development") {
    console.log("trackParkingFlow", data);
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push(data);
  }
};

export const trackArticleClick = ({
  section,
  config,
  articleTitle,
  articleDate,
  ctaText,
  link,
}: {
  section: string;
  config: string;
  articleTitle: string;
  articleDate: string;
  ctaText: string;
  link?: string;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";

  const data: Record<string, any> = {
    event: "articleClick",
    section,
    Config: config,
    articleTitle,
    articleDate,
    ctaText,
    page,
    pageTitle,
    date: new Date().toISOString().slice(0, 10),
    time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
  };

  if (link) data.link = link;

  if (process.env.NODE_ENV === "development") {
    console.log("trackArticleClick", data);
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push(data);
  }
};

export const trackStepInteraction = ({
  section,
  config,
  stepNumber,
  stepTitle,
  stepDescription,
  interactionType,
}: {
  section: string;
  config: string;
  stepNumber: number;
  stepTitle: string;
  stepDescription: string;
  interactionType: "click" | "hover";
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";

  if (process.env.NODE_ENV === "development") {
    console.log("trackStepInteraction", {
      event: "stepInteraction",
      section,
      Config: config,
      stepNumber,
      stepTitle,
      stepDescription,
      interactionType,
      page,
      pageTitle,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push({
      event: "stepInteraction",
      section,
      Config: config,
      stepNumber,
      stepTitle,
      stepDescription,
      interactionType,
      page,
      pageTitle,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
  }
};

export const trackLogoCarouselClick = ({
  section,
  config,
  logoName,
}: {
  section: string;
  config: string;
  logoName: string;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";

  if (process.env.NODE_ENV === "development") {
    console.log("trackLogoCarouselClick", {
      event: "logoClick",
      section,
      Config: config,
      logoName,
      page,
      pageTitle,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push({
      event: "logoClick",
      section,
      Config: config,
      logoName,
      page,
      pageTitle,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
  }
};

export const trackFilterSelect = ({
  event,
  section,
  config,
  filterType,
  selectedValue,
}: {
  event: string;
  section: string;
  config: string;
  filterType: string;
  selectedValue: string;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";

  if (process.env.NODE_ENV === "development") {
    console.log("trackFilterSelect", {
      event,
      section,
      Config: config,
      page,
      pageTitle,
      [filterType]: selectedValue,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push({
      event,
      section,
      Config: config,
      page,
      pageTitle,
      [filterType]: selectedValue,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
  }
};

export const trackResourceSearch = ({
  section,
  config,
  searchTerm,
}: {
  section: string;
  config: string;
  searchTerm: string;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";

  if (process.env.NODE_ENV === "development") {
    console.log("trackResourceSearch", {
      event: "resourceSearch",
      section,
      config,
      searchTerm,
      page,
      pageTitle,
    });
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push({
      event: "resourceSearch",
      section,
      Config: config,
      searchTerm,
      page,
      pageTitle,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
  }
};

export const trackFindParkingError = ({
  section,
  config,
  ctaText,
  locationInput,
  dateSelected,
  errorType,
  errorMessage,
  contentDescription,
}: {
  section: string;
  config: string;
  ctaText: string;
  locationInput: string;
  dateSelected: string;
  errorType: string;
  errorMessage: string;
  contentDescription: string;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";

  if (process.env.NODE_ENV === "development") {
    console.log("trackFindParkingError", {
      event: "find_parking_error",
      section,
      Config: config,
      ctaText,
      locationInput,
      dateSelected,
      errorType,
      errorMessage,
      contentDescription,
      page,
      pageTitle,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push({
      event: "find_parking_error",
      section,
      Config: config,
      ctaText,
      locationInput,
      dateSelected,
      errorType,
      errorMessage,
      contentDescription,
      page,
      pageTitle,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
  }
};

export const trackFindParkingClick = ({
  section,
  config,
  ctaText,
  locationInput,
  dateSelected,
  day,
  dayOfWeek,
  filterApplied,
  filterType,
  contentDescription,
}: {
  section: string;
  config: string;
  ctaText: string;
  locationInput: string;
  dateSelected: string;
  day: number;
  dayOfWeek: string;
  filterApplied: boolean;
  filterType?: string[];
  contentDescription: string;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";

  const data: Record<string, any> = {
    event: "find_parking_click",
    section,
    Config: config,
    ctaText,
    locationInput,
    dateSelected,
    day,
    dayOfWeek,
    filterApplied,
    contentDescription,
    page,
    pageTitle,
    date: new Date().toISOString().slice(0, 10),
    time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
  };

  if (filterType && filterType.length > 0) data.filterType = filterType;

  if (process.env.NODE_ENV === "development") {
    console.log("trackFindParkingClick", data);
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push(data);
  }
};

export const trackCheckAvailabilityClick = ({
  section,
  config,
  ctaText,
  parkingName,
  location,
  price,
  distance,
  address,
  contentDescription,
}: {
  section: string;
  config: string;
  ctaText: string;
  parkingName: string;
  location: string;
  price: string;
  distance: string;
  address?: string;
  contentDescription: string;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";

  if (process.env.NODE_ENV === "development") {
    console.log("trackCheckAvailabilityClick", {
      event: "check_availability_click",
      section,
      Config: config,
      ctaText,
      parkingName,
      location,
      price,
      distance,
      contentDescription,
      page,
      pageTitle,
      address,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push({
      event: "check_availability_click",
      section,
      Config: config,
      ctaText,
      parkingName,
      location,
      price,
      distance,
      contentDescription,
      page,
      pageTitle,
      address,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
  }
};

export const trackDestinationCardClick = ({
  section,
  config,
  destinationName,
  price,
  contentDescription,
}: {
  section: string;
  config: string;
  destinationName: string;
  price: string;
  contentDescription: string;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";

  const data: Record<string, any> = {
    event: "destination_card_click",
    section,
    Config: config,
    destinationName,
    price,
    contentDescription,
    page,
    pageTitle,
    date: new Date().toISOString().slice(0, 10),
    time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
  };

  if (process.env.NODE_ENV === "development") {
    console.log("trackDestinationCardClick", data);
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push(data);
  }
};

export const trackCtaSign = ({
  section,
  config,
  ctaText,
  accountType,
  contentDescription,
}: {
  section: string;
  config: string;
  ctaText: string;
  accountType: string;
  contentDescription: string;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";

  if (process.env.NODE_ENV === "development") {
    console.log("trackCtaSign", {
      event: "ctaSign",
      section,
      Config: config,
      ctaText,
      accountType,
      contentDescription,
      page,
      pageTitle,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push({
      event: "ctaSign",
      section,
      Config: config,
      ctaText,
      accountType,
      contentDescription,
      page,
      pageTitle,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
  }
};

export const trackHeaderTopLevelLinkClick = ({
  innerText,
  href,
}: {
  innerText: string;
  href: string;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";
  const device = getDevice();

  if (process.env.NODE_ENV === "development") {
    console.log("trackHeaderTopLevelLinkClick", {
      event: "Header Top Lvl Link click",
      page,
      section: "Header",
      innerText,
      elementType: "Link",
      href,
      pageTitle,
      device,
    });
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push({
      event: "Header Top Lvl Link click",
      page,
      section: "Header",
      innerText,
      elementType: "Link",
      href,
      pageTitle,
      device,
    });
  }
};

export const trackHeaderCategoryClick = ({
  innerText,
  href,
  headerCategory,
}: {
  innerText: string;
  href: string;
  headerCategory: string;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";
  const device = getDevice();

  if (process.env.NODE_ENV === "development") {
    console.log("trackHeaderCategoryClick", {
      event: "Header Category click",
      page,
      section: "Header",
      innerText,
      elementType: "Link",
      href,
      headerCategory,
      pageTitle,
      device,
    });
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push({
      event: "Header Category click",
      page,
      section: "Header",
      innerText,
      elementType: "Link",
      href,
      headerCategory,
      pageTitle,
      device,
    });
  }
};

export const trackHeaderSubCategoryClick = ({
  innerText,
  href,
  headerCategory,
}: {
  innerText: string;
  href: string;
  headerCategory: string;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";
  const device = getDevice();

  if (process.env.NODE_ENV === "development") {
    console.log("trackHeaderSubCategoryClick", {
      event: "Header Sub Category click",
      page,
      section: "Header",
      innerText,
      elementType: "Link",
      href,
      headerCategory,
      pageTitle,
      device,
    });
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push({
      event: "Header Sub Category click",
      page,
      section: "Header",
      innerText,
      elementType: "Link",
      href,
      headerCategory,
      pageTitle,
      device,
    });
  }
};

export const trackHeaderLogoClick = ({
  logoName = "ParkMobile",
  destinationURL,
}: {
  logoName?: string;
  destinationURL: string;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";

  if (process.env.NODE_ENV === "development") {
    console.log("trackHeaderLogoClick", {
      event: "logoClick",
      section: "Header",
      Config: "siteLogo",
      logoName,
      destinationURL,
      page,
      pageTitle,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push({
      event: "logoClick",
      section: "Header",
      Config: "siteLogo",
      logoName,
      destinationURL,
      page,
      pageTitle,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
  }
};

export const trackHeaderSearch = ({
  searchKeyword,
}: {
  searchKeyword: string;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";
  const device = getDevice();

  if (process.env.NODE_ENV === "development") {
    console.log("trackHeaderSearch", {
      event: "Header Search",
      section: "Header",
      Config: "headerSearch",
      elementType: "Search Input",
      searchKeyword,
      page,
      pageTitle,
      device,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push({
      event: "Header Search",
      section: "Header",
      Config: "headerSearch",
      elementType: "Search Input",
      searchKeyword,
      page,
      pageTitle,
      device,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
  }
};

export const trackHeaderSignInClick = ({
  ctaText = "Sign in",
  href,
}: {
  ctaText?: string;
  href: string;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";
  const device = getDevice();

  if (process.env.NODE_ENV === "development") {
    console.log("trackHeaderSignInClick", {
      event: "Header SignIn click",
      section: "Header",
      Config: "headerNavigation",
      ctaText,
      href,
      page,
      pageTitle,
      device,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push({
      event: "Header SignIn click",
      section: "Header",
      Config: "headerNavigation",
      ctaText,
      href,
      page,
      pageTitle,
      device,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
  }
};

export const trackFooterLinkClick = ({
  footerCategory,
  footerLinkText,
  footerLinkURL,
}: {
  footerCategory: string;
  footerLinkText: string;
  footerLinkURL: string;
}) => {
  const page = typeof window !== "undefined" ? window.location.pathname : "";
  const pageTitle = typeof document !== "undefined" ? document.title : "";

  if (process.env.NODE_ENV === "development") {
    console.log("trackFooterLinkClick", {
      event: "Footer Link click",
      section: "Footer Navigation",
      Config: "footer",
      footerCategory,
      footerLinkText,
      footerLinkURL,
      page,
      pageTitle,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push({
      event: "Footer Link click",
      section: "Footer Navigation",
      Config: "footer",
      footerCategory,
      footerLinkText,
      footerLinkURL,
      page,
      pageTitle,
      date: new Date().toISOString().slice(0, 10),
      time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
    });
  }
};

export const trackPageNotFound = () => {
  const pageURL = typeof window !== "undefined" ? window.location.href : "";

  const data = {
    event: "page_not_found",
    pageURL,
    date: new Date().toISOString().slice(0, 10),
    time: new Date().toTimeString().split(" ")[0]!.slice(0, 5),
  };

  if (process.env.NODE_ENV === "development") {
    console.log("trackPageNotFound", data);
    return;
  }

  if (window && window.dataLayer) {
    window.dataLayer.push(data);
  }
};
